import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts';
import { formatDate } from 'utils/emailHistory/dateUtils';
import dayjs from 'dayjs';

/**
 * @component EmailCountBarChart
 * @description Bar chart showing email count over time
 * @param {Object} props
 * @param {Array<{date: string|Date}>} props.data - Array of items with date property
 * @param {boolean} [props.loading=false] - Loading state
 * @param {number} [props.height=200] - Chart height
 * @param {string} [props.title] - Chart title
 * @param {string} [props.yAxisLabel] - Y-axis label
 * @param {boolean} [props.startFromToday=false] - If true, start x-axis from today instead of oldest date
 * @param {boolean} [props.futureOnly=false] - If true, only include dates >= today
 * @returns {JSX.Element}
 */
export function EmailCountBarChart({ 
  data = [], 
  loading = false,
  height = 200,
  title,
  yAxisLabel = 'Count',
  startFromToday = false,
  futureOnly = false
}) {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(800);

  // Convert yAxisLabel to lowercase for tooltip (e.g., "Bulk Sends" -> "bulk sends")
  const emailLabel = yAxisLabel.toLowerCase();

  const { chartData, xAxisLabels, allDates } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], xAxisLabels: [], allDates: [] };
    }

    // Parse dates using dayjs for better compatibility
    const itemsWithDates = data
      .map(item => {
        if (!item.date) return null;
        const parsed = dayjs(item.date);
        if (!parsed.isValid()) return null;
        return {
          ...item,
          parsedDate: parsed.toDate(),
          dayjsDate: parsed,
        };
      })
      .filter(item => item !== null)
      .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());

    if (itemsWithDates.length === 0) {
      return { chartData: [], xAxisLabels: [], allDates: [] };
    }

    // Filter out past dates if futureOnly is true
    let filteredItems = itemsWithDates;
    if (futureOnly) {
      const today = dayjs().startOf('day');
      filteredItems = itemsWithDates.filter(item => {
        const itemDayStart = item.dayjsDate.startOf('day');
        return itemDayStart.isAfter(today) || itemDayStart.isSame(today);
      });
    }

    if (filteredItems.length === 0) {
      return { chartData: [], xAxisLabels: [], allDates: [] };
    }

    // Get date range
    const today = dayjs().startOf('day');
    const dates = filteredItems.map(item => item.dayjsDate.startOf('day'));
    const timestamps = dates.map(d => d.valueOf());
    const minScheduledDate = dayjs(Math.min(...timestamps));
    const maxScheduledDate = dayjs(Math.max(...timestamps));
    
    const minDate = startFromToday ? today.toDate() : minScheduledDate.toDate();
    const maxDateObj = startFromToday && maxScheduledDate.isAfter(today)
      ? maxScheduledDate.endOf('day')
      : today.endOf('day');
    const maxDate = maxDateObj.toDate();

    // Group by date (day-level)
    const groupedByDate = {};
    filteredItems.forEach(item => {
      const dateKey = item.dayjsDate.startOf('day').format('YYYY-MM-DD');
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = 0;
      }
      groupedByDate[dateKey] += 1;
    });

    // Create array of all dates from minDate to maxDate
    const dateArray = [];
    let currentDate = dayjs(minDate).startOf('day');
    const endDate = dayjs(maxDate).startOf('day');

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      const dateKey = currentDate.format('YYYY-MM-DD');
      dateArray.push({
        date: currentDate.toDate(),
        count: groupedByDate[dateKey] || 0,
      });
      currentDate = currentDate.add(1, 'day');
    }

    // Create labels for x-axis (show labels for dates with data + first/last, limit to ~15 labels max)
    const maxLabels = 15;
    const labelInterval = Math.max(1, Math.ceil(dateArray.length / maxLabels));
    const xAxisLabels = dateArray.map((item, index) => {
      // Show label if: it's at an interval, has data, or is first/last
      const hasData = item.count > 0;
      const isInterval = index % labelInterval === 0;
      const isFirstOrLast = index === 0 || index === dateArray.length - 1;
      
      if (isInterval || hasData || isFirstOrLast) {
        return formatDate(item.date.toISOString());
      }
      return '';
    });

    const chartData = dateArray.map(item => item.count);

    return {
      chartData,
      xAxisLabels,
      allDates: dateArray,
    };
  }, [data, startFromToday, futureOnly]);

  // Measure container width for proper chart rendering
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        if (width > 0 && width !== chartWidth) {
          setChartWidth(width);
        }
      }
    };

    // Immediate measurement
    updateWidth();

    // Use requestAnimationFrame to ensure measurement happens after layout
    const rafId = requestAnimationFrame(() => {
      updateWidth();
      // Double-check after a short delay to catch any delayed layout
      setTimeout(() => {
        updateWidth();
        // One more check after a slightly longer delay for complex layouts
        setTimeout(updateWidth, 50);
      }, 10);
    });

    // Use ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(containerRef.current);

    // Also listen to window resize as fallback
    window.addEventListener('resize', updateWidth);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [data.length]); // Re-measure when data changes

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', minHeight: height }}>
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Paper>
    );
  }

  if (chartData.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', minHeight: height }}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      <BarChart
        width={chartWidth}
        height={height}
        xAxis={[
          {
            data: xAxisLabels,
            scaleType: 'band',
            tickLabelStyle: {
              fontSize: '0.7rem',
            },
          },
        ]}
        yAxis={[
          {
            tickLabelStyle: {
              fontSize: '0.75rem',
            },
          },
        ]}
        series={[
          {
            data: chartData,
            color: theme.palette.primary.main,
          },
        ]}
        slotProps={{
          tooltip: {
            trigger: 'axis',
            formatter: (params) => {
              if (!params || !Array.isArray(params) || params.length === 0) {
                return '';
              }
              const firstParam = params[0];
              const index = firstParam.dataIndex;
              if (index >= 0 && index < chartData.length && index < allDates.length) {
                const count = chartData[index];
                const dateObj = allDates[index].date;
                // Format date like "Dec 23, 2025"
                const formattedDate = formatDate(dateObj.toISOString());
                return `${count} ${emailLabel} on ${formattedDate}`;
              }
              return '';
            },
          },
        }}
        sx={{
          '& .MuiChartsAxis-root': {
            fontSize: '0.75rem',
          },
          '& .MuiChartsBarElement-root rect': {
            rx: '6px',
            ry: '6px',
          },
        }}
      />
    </Box>
  );
}

